import { SortOrderEnum } from '@shared/list-operations/components/sort-filter/sort-filter.model';
import { ObjectUtils }   from '@utils/helpers/index';

type EnumKey<Enum> = Exclude<keyof Enum, number>;
type EnumValue = string | number
type EnumLike = Record<string, EnumValue>;

function getEnumKeys<Enum extends EnumLike>( enumObj: Enum ): EnumKey<Enum>[] {
	return Object.keys( getEnumAsPlainObject( enumObj ) ) as EnumKey<Enum>[];
}

function getUniqueEnumValues<Enum extends EnumLike>( enumObj: Enum ): Enum[EnumKey<Enum>][] {
	return [ ...new Set( Object.values( getEnumAsPlainObject( enumObj ) ) ) ];
}

/**
 * Returns the enum "converted" into a plain JS object (POJO).
 * Since enums **are** plain objects at runtime anyway, the main purpose of this
 * function is to remove additional "inverse" entries resulting from the
 * "bidirectional mapping" of numeric enums
 * (cf. https://refine.dev/blog/typescript-enum/#typescript-numerical-enums-have-bidirectional-mapping;
 * i.e. enum constant `ONE = 1` at runtime will be represented by POJO
 * properties `ONE: 1` **and also** `1: 'ONE'`(!), and the function will remove
 * the latter one).
 */
function getEnumAsPlainObject<Enum extends EnumLike>( enumObj: Enum ): { [Key in EnumKey<Enum>]: Enum[Key] } {
	const enumCopy: { [Key in EnumKey<Enum>]: Enum[Key] } = { ...enumObj };
	Object.values( enumObj ?? {} ).forEach( ( value: EnumValue ) => typeof value === 'number' && delete enumCopy[ value ] );
	return enumCopy;
}

// sorts ascending by default
function sortValues( values: EnumValue[], sortOrder?: SortOrderEnum ): EnumValue[] {
	function sortComparer( a: EnumValue, b: EnumValue ): number {
		if ( typeof a === 'string' && typeof b === 'string' ) {
			return a.localeCompare( b );
		}

		if ( typeof a === 'number' && typeof b === 'number' ) {
			return a - b;
		}

		return 0;
	}

	return [ ...values ?? [] ].sort( ( a: EnumValue, b: EnumValue ) =>
		sortOrder === SortOrderEnum.DESC
		? sortComparer( b, a )
		: sortComparer( a, b )
	);
}

/**
 * FOR UNIT TESTS ONLY!
 * Do not import anywhere else than in enum-utils.spec.ts!
 */
export const UT = {
	EnumLike: {} as EnumLike,	// "hack" to get the type exported in this "encapsuled" way
	EnumValue: {} as EnumValue,	// "hack" to get the type exported in this "encapsuled" way
	getEnumKeys,
	getEnumAsPlainObject,
	getUniqueEnumValues,
	sortValues
}


function getEnumKeyByValue( enumObj: object, value: string ): string {
	const entry = Object.entries( enumObj ?? {} ).find( ( [ key, val ] ) => val === value );
	return entry ? entry[ 0 ] : null;
}


function getEnumKeysByValues( enumObj: object, values: string[] ): string[] {
	return values?.map( value => getEnumKeyByValue( enumObj, value ) )?.filter( value => !ObjectUtils.isNullish( value ) ) ?? null;
}


function getEnumsKeysAsList( enumObj: object, sortOrder?: SortOrderEnum, removeNumericKeys?: boolean ): string[] {
	const keys = removeNumericKeys
							 ? getEnumKeys( enumObj as EnumLike )
							 : Object.keys( enumObj ?? {} );

	return sortOrder ? sortValues( keys, sortOrder ) as string[] : keys;
}


function getEnumsValuesAsList( enumObj: object, sortOrder?: SortOrderEnum, removeDuplicates?: boolean ): string[] {
	const values = removeDuplicates
								 ? getUniqueEnumValues( enumObj as EnumLike )
								 : Object.values( enumObj ?? {} );

	return sortOrder ? sortValues( values, sortOrder ) : values;
}


/**
 * Sorts an array of enum values according to the original order of these values
 * in the actual enum.
 * @param enumObj The enum by which the values are to be sorted.
 * @param valuesToBeSorted The array of enum constants to be sorted according to
 * the order of the constants in `enumObj`.<br>
 *
 * Note: The function is type-safe, i.e. the compiler will enforce that
 * `valuesToBeSorted` is an array of constants of the enum passed to `enumObj`.
 * However, it is not necessary and the code won't even compile if the enum
 * is passed as generics parameter. **So don't use the generics parameter!**
 */
function sortByOriginalEnumOrder<Enum extends EnumLike>( enumObj: Enum, valuesToBeSorted: Enum[keyof Enum][] ): Enum[keyof Enum][] {
	const enumOrder = Object.values( enumObj ?? {} );
	return [ ...valuesToBeSorted ?? [] ].sort( ( a, b ) => enumOrder.indexOf( a ) - enumOrder.indexOf( b ) );
}


export const publicFunctions = {
	getEnumKeyByValue,
	getEnumKeysByValues,
	getEnumsKeysAsList,
	getEnumsValuesAsList,
	sortByOriginalEnumOrder
};
